import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Plus, Check, X, Smartphone, Watch, Hospital, Activity } from 'lucide-react';
import { AddDataSourceDialog } from './AddDataSourceDialog';
import { DataSourceSettingsDialog } from './DataSourceSettingsDialog';
import { useAppleHealth } from '@/hooks/useAppleHealth';

interface DataSource {
  id: string;
  name: string;
  type: 'health_system' | 'wearable' | 'app' | 'manual';
  status: 'connected' | 'disconnected' | 'pending';
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  lastSync?: string;
  enabled: boolean;
}

export const DataSources = () => {
  const { isAvailable, hasPermissions, isLoading, requestPermissions } = useAppleHealth();
  
  const [sources, setSources] = useState<DataSource[]>([
    {
      id: '1',
      name: 'Apple Health',
      type: 'app',
      status: 'disconnected',
      icon: Smartphone,
      description: 'Steps, heart rate, and health records',
      enabled: false
    },
    {
      id: '2',
      name: 'Apple Watch',
      type: 'wearable',
      status: 'disconnected',
      icon: Watch,
      description: 'Continuous heart rate and activity tracking',
      enabled: false
    },
    {
      id: '3',
      name: 'City General Hospital',
      type: 'health_system',
      status: 'connected',
      icon: Hospital,
      description: 'Lab results, appointments, and medical records',
      lastSync: '1 hour ago',
      enabled: true
    },
    {
      id: '4',
      name: 'Google Fit',
      type: 'app',
      status: 'disconnected',
      icon: Activity,
      description: 'Activity tracking and fitness data',
      enabled: false
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  // Update Apple Health and Apple Watch status based on permissions
  // Note: Apple Watch data comes through HealthKit, so it uses the same permissions
  useEffect(() => {
    if (!isLoading) {
      setSources(prevSources =>
        prevSources.map(source =>
          source.name === 'Apple Health' || source.name === 'Apple Watch'
            ? {
                ...source,
                status: hasPermissions ? 'connected' : 'disconnected',
                enabled: hasPermissions,
                lastSync: hasPermissions ? 'Just now' : undefined
              }
            : source
        )
      );
    }
  }, [hasPermissions, isLoading]);

  const handleAddSource = (healthSystem: { id: string; name: string; description?: string }) => {
    // Convert the search result to our DataSource format
    const dataSource: DataSource = {
      id: (sources.length + 1).toString(),
      name: healthSystem.name,
      type: 'health_system',
      status: 'pending',
      icon: Hospital, // Health systems use Hospital icon
      description: healthSystem.description || 'New health system',
      enabled: true
    };

    setSources([...sources, dataSource]);
    console.log('Added new health system:', dataSource);
  };

  const toggleSource = async (id: string) => {
    const source = sources.find(s => s.id === id);
    
    // Special handling for Apple Health and Apple Watch - need to request permissions
    // Note: Apple Watch data comes through HealthKit, so it uses the same permissions
    if ((source?.name === 'Apple Health' || source?.name === 'Apple Watch') && !source.enabled) {
      if (!isAvailable) {
        console.warn('Apple Health is not available on this device');
        return;
      }
      
      try {
        const granted = await requestPermissions();
        if (granted) {
          setSources(sources.map(s => 
            s.id === id 
              ? { ...s, enabled: true, status: 'connected' as const, lastSync: 'Just now' }
              : s
          ));
        }
      } catch (error) {
        console.error('Failed to request Apple Health permissions:', error);
      }
      return;
    }
    
    // For other sources or disconnecting Apple Health/Apple Watch
    setSources(sources.map(source => 
      source.id === id 
        ? { ...source, enabled: !source.enabled }
        : source
    ));
  };

  const handleReconnect = async (id: string) => {
    const source = sources.find(s => s.id === id);
    
    // Special handling for Apple Health and Apple Watch - need to request permissions
    // Note: Apple Watch data comes through HealthKit, so it uses the same permissions
    if (source?.name === 'Apple Health' || source?.name === 'Apple Watch') {
      if (!isAvailable) {
        console.warn('Apple Health is not available on this device');
        return;
      }
      
      try {
        const granted = await requestPermissions();
        if (granted) {
          setSources(sources.map(s => 
            s.id === id 
              ? { ...s, status: 'connected' as const, enabled: true, lastSync: 'Just now' }
              : s
          ));
        }
      } catch (error) {
        console.error('Failed to request Apple Health permissions:', error);
      }
      return;
    }
    
    // For other sources
    setSources(sources.map(source => 
      source.id === id 
        ? { ...source, status: 'connected' as const, lastSync: 'Just now' }
        : source
    ));
  };

  const handleDisconnect = (id: string) => {
    // Note: For Apple Health, we can't actually revoke permissions programmatically
    // This just updates the UI state. User would need to revoke in iOS Settings.
    setSources(sources.map(source => 
      source.id === id 
        ? { ...source, status: 'disconnected' as const, enabled: false }
        : source
    ));
  };

  const handleRemove = (id: string) => {
    setSources(sources.filter(source => source.id !== id));
  };

  const handleConnect = async (sourceName: string, sourceType: 'wearable' | 'app', icon: React.ComponentType<{ className?: string }>, description: string) => {
    // Check if source is already connected
    const existingSource = sources.find(s => s.name === sourceName);
    if (existingSource) {
      console.log(`${sourceName} is already connected`);
      return;
    }

    // Add source with pending status
    const newSource: DataSource = {
      id: (sources.length + 1).toString(),
      name: sourceName,
      type: sourceType,
      status: 'pending',
      icon: icon,
      description: description,
      enabled: true
    };

    setSources([...sources, newSource]);

    // Simulate connection process
    // In a real implementation, this would:
    // - For OAuth-based services (Fitbit, Garmin, Strava): Initiate OAuth flow
    // - For native services (Apple Health, Google Fit): Request native permissions
    // - For web services: Open connection dialog or redirect to OAuth provider
    
    try {
      // Simulate async connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update status to connected after successful connection
      setSources(prevSources =>
        prevSources.map(source =>
          source.id === newSource.id
            ? { ...source, status: 'connected' as const, lastSync: 'Just now' }
            : source
        )
      );

      console.log(`Successfully connected ${sourceName}`);
    } catch (error) {
      console.error(`Error connecting ${sourceName}:`, error);
      // Update status to disconnected on error
      setSources(prevSources =>
        prevSources.map(source =>
          source.id === newSource.id
            ? { ...source, status: 'disconnected' as const }
            : source
        )
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'disconnected': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <Check className="h-4 w-4" />;
      case 'pending': return <X className="h-4 w-4" />;
      case 'disconnected': return <X className="h-4 w-4" />;
      default: return <X className="h-4 w-4" />;
    }
  };

  const availableSources: Array<{
    name: string;
    type: 'wearable' | 'app';
    icon: React.ComponentType<{ className?: string }>;
    description: string;
  }> = [
    { name: 'Fitbit', type: 'wearable' as const, icon: Watch, description: 'Activity, sleep, and heart rate data' },
    { name: 'Garmin', type: 'wearable' as const, icon: Watch, description: 'Advanced fitness and health metrics' },
    { name: 'MyFitnessPal', type: 'app' as const, icon: Activity, description: 'Nutrition and calorie tracking' },
    { name: 'Strava', type: 'app' as const, icon: Activity, description: 'Social fitness tracking' },
    { name: 'Google Fit', type: 'app' as const, icon: Activity, description: 'Activity tracking and fitness data' },
    { name: 'Samsung Health', type: 'app' as const, icon: Activity, description: 'Comprehensive health platform' }
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Data Sources</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Connect your health data sources to get a complete Health Score
          </p>
        </div>
        <div className="w-full md:w-auto">
          <AddDataSourceDialog onAddSource={handleAddSource} />
        </div>
      </div>

      {/* Connected Sources */}
      <Card className="p-4 md:p-6">
        <h3 className="text-lg font-semibold mb-4">Connected Sources</h3>
        <div className="space-y-4">
          {sources.map((source) => (
            <div key={source.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 md:p-4 rounded-lg border">
              <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <source.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-medium">{source.name}</h4>
                    <Badge className={`flex items-center space-x-1 text-xs ${getStatusColor(source.status)}`}>
                      {getStatusIcon(source.status)}
                      <span className="capitalize">{source.status}</span>
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{source.description}</p>
                  {source.lastSync && (
                    <p className="text-xs text-muted-foreground mt-1">Last sync: {source.lastSync}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end sm:justify-start space-x-2 sm:space-x-4 flex-shrink-0">
                <Switch
                  checked={source.enabled}
                  onCheckedChange={() => toggleSource(source.id)}
                />
                <DataSourceSettingsDialog
                  source={source}
                  onReconnect={handleReconnect}
                  onDisconnect={handleDisconnect}
                  onRemove={handleRemove}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Available Sources */}
      <Card className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Available Apps & Wearables</h3>
          <div className="w-full sm:w-64">
            <Input
              placeholder="Search sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableSources
            .filter(source =>
              source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              source.description.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((source, index) => (
            <div key={index} className="p-3 md:p-4 rounded-lg border border-dashed border-gray-300 hover:border-primary hover:bg-primary/5 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <source.icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium">{source.name}</h4>
                    <p className="text-sm text-muted-foreground">{source.description}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => handleConnect(source.name, source.type, source.icon, source.description)}
                  disabled={sources.some(s => s.name === source.name)}
                >
                  {sources.some(s => s.name === source.name) ? 'Connected' : 'Connect'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Data Sync Settings */}
      <Card className="p-4 md:p-6">
        <h3 className="text-lg font-semibold mb-4">Sync Settings</h3>
        <div className="space-y-4">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex-1">
              <h4 className="font-medium">Auto-sync enabled</h4>
              <p className="text-sm text-muted-foreground">
                Automatically sync data every 15 minutes
              </p>
            </div>
            <Switch defaultChecked className="flex-shrink-0" />
          </div>
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex-1">
              <h4 className="font-medium">Background sync</h4>
              <p className="text-sm text-muted-foreground">
                Allow syncing when app is in background
              </p>
            </div>
            <Switch defaultChecked className="flex-shrink-0" />
          </div>
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex-1">
              <h4 className="font-medium">WiFi only</h4>
              <p className="text-sm text-muted-foreground">
                Only sync when connected to WiFi
              </p>
            </div>
            <Switch className="flex-shrink-0" />
          </div>
        </div>
      </Card>
    </div>
  );
};
